/*
 * Smooth grid updates  ·  assets/hoh-grid-flip.js
 *
 * Why this exists
 * ---------------
 * The Hall of Heroes grids (main gallery, Your Hall sub-views, Fresh Faces
 * rail) all used to do `container.innerHTML = newCards.map(build).join('')`
 * on every sort, filter, search keystroke, or dismiss. That nukes every
 * <img> tag, so cards flash to dark placeholder until each image decodes
 * again, even if it's already cached. Reordering felt like the whole grid
 * blinked black, then appeared in the new arrangement.
 *
 * This module exposes one helper:
 *
 *   window.hohSmoothGridUpdate(container, items, options)
 *
 *     options.key(item)         -> string id (required)
 *     options.build(item, idx)  -> HTML string for one card (required)
 *     options.duration          -> ms for the FLIP slide (default 240)
 *     options.leaveDuration     -> ms for fading-out cards (default 200)
 *     options.snapThreshold     -> if changed cards > this, just snap
 *                                  without animating (default 60)
 *     options.prepareEntering   -> fn(el) called on each new card before
 *                                  it gets inline styles. Use this to add
 *                                  classes the card needs to be visible
 *                                  (e.g. .hoh-visible on main-grid cards).
 *     options.afterRender       -> fn(container) called once the whole
 *                                  pass settles.
 *
 * It uses the FLIP technique (First, Last, Invert, Play):
 *   1. Snapshot every existing card's bounding rect, keyed by data-hero-id.
 *   2. Diff: cards in the new list become "kept" (move) or "entering";
 *      old cards no longer in the list become "leaving".
 *   3. Reorder kept nodes in-place; insert new nodes; mark leavers with a
 *      fade-out class and remove them after the leave animation.
 *   4. After reorder, measure new positions. For each kept card, apply an
 *      inverse transform that snaps it back to its old position visually.
 *   5. Next frame, transition transform back to identity. Cards slide.
 *
 * Crucially: kept cards keep the same DOM nodes, so their <img> elements
 * never reload. No flash. Heart counts, hearted-state classes, and any
 * MutationObserver-bound buttons all survive.
 *
 * Respects prefers-reduced-motion (snaps instantly).
 * Snaps on very large changesets too, since animating 200 cards at once
 * can jank low-end phones more than the flash ever did.
 */
(function(){
  'use strict';

  /* Default timings tuned to feel responsive but not rushed. Mobile gets
     a slightly shorter slide so 2-col grids don't drag. */
  var IS_MOBILE = (function(){
    try { return window.matchMedia('(max-width: 700px)').matches; } catch(e){ return false; }
  })();
  var DEFAULT_DURATION = IS_MOBILE ? 200 : 240;
  var DEFAULT_LEAVE = IS_MOBILE ? 170 : 200;

  function prefersReducedMotion(){
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    catch(e){ return false; }
  }

  /* Build a fresh DOM tree from an HTML string. Using a template element
     means script tags don't auto-execute and we get real Node refs out. */
  function htmlToNodes(html){
    var t = document.createElement('template');
    t.innerHTML = html;
    return Array.prototype.slice.call(t.content.childNodes);
  }

  /* Pull the first element child out of a fragment built from a card's
     HTML. Card HTML is always a single root element (a div). */
  function buildCardEl(html){
    var nodes = htmlToNodes(html);
    for(var i = 0; i < nodes.length; i++){
      if(nodes[i].nodeType === 1) return nodes[i];
    }
    return null;
  }

  /* Read the data-hero-id (main grid + Your Hall) or data-id (Fresh Faces)
     attribute off a card element, whichever is present. */
  function readKey(el){
    if(!el || el.nodeType !== 1) return null;
    return el.getAttribute('data-hero-id') || el.getAttribute('data-id') || null;
  }

  /* Snapshot every direct-child element's bounding rect, keyed by id.
     Cards that are scrolled far off-screen still get measured because
     getBoundingClientRect is cheap; we filter wasted animations later. */
  function snapshotRects(container){
    var map = {};
    var children = container.children;
    for(var i = 0; i < children.length; i++){
      var el = children[i];
      /* Skip nodes that are mid-fade-out from a previous pass. Their
         rect would be the absolute-positioned final spot, which would
         confuse FLIP if the user triggered a second change rapidly. */
      if(el.classList && el.classList.contains('hoh-grid-leaving')) continue;
      var k = readKey(el);
      if(!k) continue;
      map[k] = el.getBoundingClientRect();
    }
    return map;
  }

  /* Returns true if a rect is far enough off-screen that animating it
     would be invisible work. We still keep the node and skip the FLIP
     transform on it. */
  function isFarOffscreen(rect){
    if(!rect) return true;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    var slack = 600;
    return rect.bottom < -slack || rect.top > vh + slack;
  }

  function clearTransform(el){
    el.style.transform = '';
    el.style.transition = '';
    el.style.willChange = '';
  }

  /* The main entry point. */
  window.hohSmoothGridUpdate = function(container, items, options){
    if(!container || !options || typeof options.key !== 'function' || typeof options.build !== 'function'){
      return;
    }
    items = items || [];
    var keyFn = options.key;
    var buildFn = options.build;
    var duration = (typeof options.duration === 'number') ? options.duration : DEFAULT_DURATION;
    var leaveDuration = (typeof options.leaveDuration === 'number') ? options.leaveDuration : DEFAULT_LEAVE;
    var snapThreshold = (typeof options.snapThreshold === 'number') ? options.snapThreshold : 60;

    var prepareEntering = (typeof options.prepareEntering === 'function') ? options.prepareEntering : null;
    /* R140-P: Optional callback invoked on each REUSED node (FLIP
       preserves the DOM node when its key is in both old and new
       renders). Call sites use this to clear transient state classes
       — e.g., Fresh Faces clears .hoh-fresh-card-spent-heart so the
       heart button works on a card whose owner just appeared in a
       new batch. Without this hook, the spent-heart lock from a
       previous render persisted and the user couldn't re-heart the
       same character even though the new render logically cleared
       the state. */
    var prepareReused = (typeof options.prepareReused === 'function') ? options.prepareReused : null;

    /* First-render fast path: container is empty, just paint. */
    if(!container.children.length){
      var html = items.map(buildFn).join('');
      container.innerHTML = html;
      var kids = container.children;
      if(prepareEntering){
        for(var pi = 0; pi < kids.length; pi++) prepareEntering(kids[pi]);
      }
      /* No fade-in on first paint: the section's own load animation
         already handles initial reveal via observeElements. */
      if(typeof options.afterRender === 'function') options.afterRender(container);
      return;
    }

    /* 1. Snapshot existing positions. */
    var oldRects = snapshotRects(container);
    var oldNodes = {};
    var oldChildren = Array.prototype.slice.call(container.children);
    oldChildren.forEach(function(el){
      var k = readKey(el);
      if(k) oldNodes[k] = el;
    });

    /* 2. Compute new key list and find leavers / enterers. */
    var newKeys = items.map(keyFn).map(String);
    var newKeySet = {};
    newKeys.forEach(function(k){ newKeySet[k] = true; });
    var leavers = [];
    Object.keys(oldNodes).forEach(function(k){
      if(!newKeySet[k]) leavers.push(oldNodes[k]);
    });
    var enteringCount = 0;
    newKeys.forEach(function(k){ if(!oldNodes[k]) enteringCount++; });

    /* Snap fast path: too many changes, or reduced-motion. Just rewrite.
       Set inline opacity:1 on every card so any base hide rule is
       overridden without needing IntersectionObserver to trip. */
    var changeCount = leavers.length + enteringCount;
    if(prefersReducedMotion() || changeCount > snapThreshold){
      container.innerHTML = items.map(buildFn).join('');
      var snapKids = container.children;
      for(var si = 0; si < snapKids.length; si++){
        snapKids[si].style.opacity = '1';
        if(prepareEntering) prepareEntering(snapKids[si]);
      }
      if(typeof options.afterRender === 'function') options.afterRender(container);
      return;
    }

    /* 3. Build a fragment of the desired final order. For each item,
       reuse the existing node if present, otherwise build a new one. */
    var enteringNodes = [];
    var orderedNodes = items.map(function(item, i){
      var k = String(keyFn(item));
      var existing = oldNodes[k];
      if(existing){
        /* R140-P: Run the optional prepareReused hook so the call
           site can clear stale transient classes (spent-heart, etc.)
           on cards whose key is being reused for a new render. */
        if(prepareReused) try { prepareReused(existing); } catch(_){}
        return existing;
      }
      var fresh = buildCardEl(buildFn(item, i));
      if(fresh) enteringNodes.push(fresh);
      return fresh;
    }).filter(Boolean);

    /* 4. Remove leavers immediately from layout flow but keep them in the
       DOM with absolute positioning so they fade in their final spot
       without dragging neighbors. We position them at their old rect
       relative to the container. */
    var containerRect = container.getBoundingClientRect();
    var containerPos = window.getComputedStyle(container).position;
    if(containerPos === 'static'){
      /* Make container the offset parent for absolute children. */
      container.style.position = 'relative';
    }
    leavers.forEach(function(el){
      var r = oldRects[readKey(el)];
      if(!r) { container.removeChild(el); return; }
      el.style.position = 'absolute';
      el.style.left = (r.left - containerRect.left) + 'px';
      el.style.top = (r.top - containerRect.top) + 'px';
      /* Force border-box so the inline width/height equal the rendered
         size; otherwise content-box cards bloat by their border width
         when the absolute positioning kicks in. */
      el.style.boxSizing = 'border-box';
      el.style.width = r.width + 'px';
      el.style.height = r.height + 'px';
      el.style.margin = '0';
      el.style.zIndex = '1';
      el.style.pointerEvents = 'none';
      el.classList.add('hoh-grid-leaving');
    });

    /* 5. Reorder kept nodes + insert new ones in the desired sequence.
       We append in order to a fragment, then replace container's
       non-leaver children. Using appendChild on existing nodes moves
       them rather than copying. */
    var frag = document.createDocumentFragment();
    orderedNodes.forEach(function(n){ frag.appendChild(n); });
    /* Remove every direct child that's not a leaver (leavers are absolutely
       positioned so they won't disturb layout) before inserting the
       fragment at the start. */
    var toRemove = [];
    for(var ci = 0; ci < container.children.length; ci++){
      var ch = container.children[ci];
      if(!ch.classList.contains('hoh-grid-leaving')) toRemove.push(ch);
    }
    toRemove.forEach(function(ch){ container.removeChild(ch); });
    container.insertBefore(frag, container.firstChild);

    /* 6. Mark entering nodes so they fade up from a slight scale. They
       won't get a FLIP transform since they had no old rect. The
       prepareEntering hook runs first so the caller can add classes
       (e.g. .hoh-visible) the card needs to override base styles. */
    enteringNodes.forEach(function(el){
      if(prepareEntering) prepareEntering(el);
      el.style.opacity = '0';
      el.style.transform = 'scale(0.94)';
      el.style.transition = 'none';
      el.style.willChange = 'transform, opacity';
    });

    /* 7. Measure new positions for kept nodes and apply inverse transform
       so they visually appear at their old position. */
    /* PERF-3 (2026-08-06): measure everything FIRST, then write. The old
       single pass alternated getBoundingClientRect with style writes, so
       every card after the first forced a fresh synchronous layout — N
       cards, N layouts, on every sort, filter and search keystroke. A
       transform never moves a sibling's layout box, so the two-pass split
       computes the identical dx/dy against the identical geometry and the
       browser lays out once. */
    var keptInverses = [];
    var pendingMoves = [];
    orderedNodes.forEach(function(el){
      var k = readKey(el);
      if(!k) return;
      var oldRect = oldRects[k];
      if(!oldRect) return; /* entering, handled above */
      var newRect = el.getBoundingClientRect();
      var dx = oldRect.left - newRect.left;
      var dy = oldRect.top - newRect.top;
      if(Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return; /* stayed in place */
      /* Never FLIP across the length of the Hall. A Most/Least reversal can
         move a card from ~90,000px below the viewport into the first row.
         Applying that full inverse transform puts the card back off-screen;
         content-visibility:auto then suspends its transition, so it stays
         there permanently and only the pinned Featured card appears. Animate
         only when BOTH endpoints are near enough to paint. Distant arrivals
         land in their new slot immediately and use the gallery reveal fade. */
      if(isFarOffscreen(oldRect) || isFarOffscreen(newRect)) return;
      pendingMoves.push({ el: el, dx: dx, dy: dy });
    });
    pendingMoves.forEach(function(m){
      m.el.style.transform = 'translate(' + m.dx + 'px,' + m.dy + 'px)';
      m.el.style.transition = 'none';
      m.el.style.willChange = 'transform';
      keptInverses.push(m.el);
    });

    /* 8. Next frame: release transforms, animate to identity. Entering
       cards animate to opacity:1 explicitly (not '') so any base CSS
       opacity:0 hide rule (e.g. .hoh-card pre-IntersectionObserver) is
       permanently overridden. The user triggered this change, so the
       scroll-in fade pattern doesn't apply here. */
    requestAnimationFrame(function(){
      requestAnimationFrame(function(){
        keptInverses.forEach(function(el){
          el.style.transition = 'transform ' + duration + 'ms cubic-bezier(0.22, 0.61, 0.36, 1)';
          el.style.transform = '';
        });
        enteringNodes.forEach(function(el){
          el.style.transition = 'opacity ' + duration + 'ms ease, transform ' + duration + 'ms cubic-bezier(0.22, 0.61, 0.36, 1)';
          el.style.opacity = '1';
          el.style.transform = 'none';
        });
        leavers.forEach(function(el){
          el.style.transition = 'opacity ' + leaveDuration + 'ms ease, transform ' + leaveDuration + 'ms ease';
          el.style.opacity = '0';
          el.style.transform = (el.style.transform || '') + ' scale(0.92)';
        });
      });
    });

    /* 9. After the animations finish, clean inline styles on survivors and
       remove leavers from the DOM. Entering nodes keep opacity:1 inline
       so they don't fall back to a base hide rule. */
    setTimeout(function(){
      keptInverses.forEach(clearTransform);
      enteringNodes.forEach(function(el){
        el.style.transition = '';
        el.style.willChange = '';
        /* Leave opacity:1 and transform:none in place. */
      });
      leavers.forEach(function(el){
        if(el.parentNode) el.parentNode.removeChild(el);
      });
      if(typeof options.afterRender === 'function') options.afterRender(container);
    }, Math.max(duration, leaveDuration) + 30);
  };

  /* Convenience: wrap an empty-state element with a smooth crossfade
     against a grid container. Caller decides whether grid is empty
     and which element to show. */
  window.hohSmoothEmptyState = function(grid, empty, isEmpty){
    if(!grid || !empty) return;
    var reduce = prefersReducedMotion();
    var dur = reduce ? 0 : 150;
    if(isEmpty){
      grid.style.transition = 'opacity ' + dur + 'ms ease';
      grid.style.opacity = '0';
      setTimeout(function(){
        grid.style.display = 'none';
        grid.style.opacity = '';
        grid.style.transition = '';
        empty.style.display = 'block';
        empty.style.opacity = '0';
        empty.style.transition = 'opacity ' + dur + 'ms ease';
        requestAnimationFrame(function(){ empty.style.opacity = '1'; });
        setTimeout(function(){ empty.style.transition = ''; empty.style.opacity = ''; }, dur + 30);
      }, dur);
    } else {
      if(empty.style.display !== 'none'){
        empty.style.transition = 'opacity ' + dur + 'ms ease';
        empty.style.opacity = '0';
        setTimeout(function(){
          empty.style.display = 'none';
          empty.style.opacity = '';
          empty.style.transition = '';
        }, dur);
      }
      grid.style.display = 'grid';
    }
  };
})();
